const DB_NAME = "gb-domain-fallback";
const DB_VERSION = 1;
const STORE_NAME = "domains";
const RECORD_ID = "primary";
const LOCAL_STORAGE_KEY = "__gb_backup_domains";
// 与 public/sw.js 中 DOMAINS_CACHE_NAME / DOMAINS_CACHE_KEY 保持一致
const CACHE_NAME = "gb-domain-fallback-cache";
const CACHE_KEY = "/__internal__/backup-domains.json";

interface BackupDomainsRecord {
  id: typeof RECORD_ID;
  list: string[];
  updatedAt: number;
  sourceOrigin: string;
}

function normalizeDomains(list: unknown): string[] {
  if (!Array.isArray(list)) return [];
  return Array.from(
    new Set(
      list
        .map(item =>
          String(item || "")
            .trim()
            .toLowerCase()
        )
        .filter(Boolean)
    )
  );
}

function saveToLocalStorage(list: string[], sourceOrigin = location.origin) {
  try {
    localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify({
        id: RECORD_ID,
        list,
        updatedAt: Date.now(),
        sourceOrigin,
      })
    );
  } catch {
    // localStorage may be unavailable in private mode.
  }
}

function readFromLocalStorage(): string[] {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return [];
    const record = JSON.parse(raw) as Partial<BackupDomainsRecord>;
    return normalizeDomains(record.list);
  } catch {
    return [];
  }
}

async function saveToCacheStorage(
  list: string[],
  sourceOrigin = location.origin
): Promise<void> {
  if (typeof caches === "undefined") return;
  try {
    const cache = await caches.open(CACHE_NAME);
    const body = JSON.stringify({
      id: RECORD_ID,
      list,
      updatedAt: Date.now(),
      sourceOrigin,
    });
    await cache.put(
      CACHE_KEY,
      new Response(body, {
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-store",
        },
      })
    );
  } catch {
    // Cache Storage 不可用时静默失败，已有 IndexedDB / localStorage 兜底
  }
}

async function readFromCacheStorage(): Promise<string[]> {
  if (typeof caches === "undefined") return [];
  try {
    const cache = await caches.open(CACHE_NAME);
    const res = await cache.match(CACHE_KEY);
    if (!res) return [];
    const record = (await res.json()) as Partial<BackupDomainsRecord>;
    return normalizeDomains(record.list);
  } catch {
    return [];
  }
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function readFromIndexedDB(): Promise<string[]> {
  if (!("indexedDB" in window)) return [];
  try {
    const db = await openDB();
    return await new Promise<string[]>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(RECORD_ID);

      req.onsuccess = () => {
        resolve(normalizeDomains((req.result as BackupDomainsRecord)?.list));
      };
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
      tx.onerror = () => db.close();
    });
  } catch {
    return [];
  }
}

export async function getBackupDomains(): Promise<string[]> {
  // 顺序：IndexedDB → Cache Storage → localStorage，任一命中即返回
  const fromIDB = await readFromIndexedDB();
  if (fromIDB.length > 0) return fromIDB;

  const fromCache = await readFromCacheStorage();
  if (fromCache.length > 0) return fromCache;

  return readFromLocalStorage();
}

export async function saveBackupDomains(
  list: string[],
  sourceOrigin = location.origin
): Promise<void> {
  const normalized = normalizeDomains(list);
  // 三层并写：localStorage（同步）+ Cache Storage（SW 主用）+ IndexedDB
  saveToLocalStorage(normalized, sourceOrigin);
  // Cache Storage 写入失败也不影响主流程，与 IndexedDB 并行
  const cachePromise = saveToCacheStorage(normalized, sourceOrigin);

  if (!("indexedDB" in window)) {
    await cachePromise;
    return;
  }

  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      store.put({
        id: RECORD_ID,
        list: normalized,
        updatedAt: Date.now(),
        sourceOrigin,
      } satisfies BackupDomainsRecord);

      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  } catch {
    // IndexedDB 异常时 localStorage / Cache Storage 仍有兜底
  }

  await cachePromise;
}
